import { IonCard } from '@ionic/react'
import { Skeleton } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { co2PerKwh, electricTaxPerKwh } from '../../../../constants'
import { ConsumptionDataContext } from '../../../../contexts/ConsumptionDataContext'
import useUser from '../../../../contexts/hooks/useUser'
import { ModalContext } from '../../../../contexts/ModalContext'
import { ObjectContext } from '../../../../contexts/objectContext/ObjectContext'
import { UserContext } from '../../../../contexts/UserContext'
import { getAtDayString } from '../../../../modules/consumptionDataParser'
import { trpc } from '../../../../modules/trpc'
import { SolarPanelHook } from '../../../devices/solarPanel/hooks/useSolarPanel'
import OverviewComparisonCard from './OverviewComparisonCard'

interface Props {
	consumptionData: any
	inverter: SolarPanelHook | undefined
	activeView: 'nature' | 'consumption' | 'money' | 'production'
}

export const initialPrevCard = {
	title: '-',
	value: '0',
	lastValue: '-',
	positive: true,
	unit: '',
}

const PreviousCard: React.FC<Props> = ({ consumptionData, inverter, activeView }) => {
	const utils = trpc.useContext()

	const { lastAvailableConsumptionData } = useContext(ConsumptionDataContext)
	const object = useContext(ObjectContext)
	const { isAnonymousUser, logOut } = useContext(UserContext)
	const { insertModal } = useContext(ModalContext)

	const { user } = useUser()
	const history = useHistory()
	const { t } = useTranslation('pages_overview')

	const [firstCardContent, setFirstCardContent] = useState(initialPrevCard)
	const [secondCardContent, setSecondCardContent] = useState(initialPrevCard)

	const [lastAvailableInvoiceAcc, setLastAvailableInvoiceAcc] = useState<number | null>()
	const [beforeLastAvailableInvoiceAcc, setBeforeLastAvailableInvoiceAcc] = useState<
		number | null
	>()
	const [lastWeekInvoiceAcc, setLastWeekInvoiceAcc] = useState<number | null>()
	const [thisWeekInvoiceAcc, setThisWeekInvoiceAcc] = useState<number | null>()

	const { consideredDay, comparisonDay, consideredWeek, comparisonWeek } = consumptionData

	const setElectricityCard = () => {
		const thisWeekNumber = dayjs().isoWeek()
		const hasThisWeekData = consideredWeek.localTime.weekNumber === thisWeekNumber

		//Make sure this week is this week
		const thisWeek = hasThisWeekData ? consideredWeek : { kwh: 0 }
		const lastWeek = hasThisWeekData ? comparisonWeek : { kwh: consideredWeek.kwh }

		setFirstCardContent({
			title: getAtDayString(consideredDay.localTime?.weekDay, user!.language) ?? 'loading...',
			value: consideredDay.kwh?.toFixed(0) ?? 0,
			lastValue: `${comparisonDay?.kwh?.toFixed(0) ?? 0}`,
			positive: consideredDay.kwh > comparisonDay?.kwh,
			unit: 'kWh',
		})

		setSecondCardContent({
			title: t('previousCard.tamaViikko'),
			value: thisWeek.kwh.toFixed(0),
			lastValue: `${lastWeek.kwh.toFixed(0)}`,
			positive: thisWeek.kwh > lastWeek.kwh,
			unit: 'kWh',
		})
	}

	const setNatureCard = () => {
		setFirstCardContent({
			title: getAtDayString(consideredDay.localTime?.weekDay, user!.language) ?? 'loading...',
			value: (consumptionData.consideredDay.kwh * co2PerKwh).toFixed(2) ?? 0,
			lastValue: (consumptionData.comparisonDay.kwh * co2PerKwh).toFixed(2) ?? 0,
			positive: consideredDay.kwh > comparisonDay?.kwh,
			unit: 'kg',
		})
		setSecondCardContent({
			title: t('previousCard.tamaViikko'),
			value: (consumptionData.consideredWeek.kwh * co2PerKwh).toFixed(2) ?? 0,
			lastValue: (consumptionData.comparisonWeek.kwh * co2PerKwh).toFixed(2),
			positive: consideredWeek.kwh > comparisonWeek.kwh,
			unit: 'kg',
		})
	}

	const getInvoiceAccData = async (
		objectId: number,
		price: number,
		isSpotMargin: boolean,
		mode: string,
		lastAvailableConsumptionData: Dayjs
	): Promise<number | null> => {
		if (!lastAvailableConsumptionData) return null

		switch (mode) {
			case 'lastAvailable': {
				if (!lastAvailableConsumptionData) return null

				const lastAvailableConsumptionDataString = lastAvailableConsumptionData.format()
				const lastAvailableConsumptionDataStringMinusOne = lastAvailableConsumptionData
					.endOf('day')
					.format()
				const to = lastAvailableConsumptionDataStringMinusOne
				const from = lastAvailableConsumptionDataString

				const invoice = {
					objectId,
					from,
					to,
					price,
					isSpotMargin,
					groupBy: 'month' as const,
				}

				const results = await utils.objects.getInvoiceAccumulation.fetch(invoice)

				return results[0]?.invoiceAccumulationInEur ?? 0
			}
			case 'beforeLastAvailable': {
				const from = dayjs(lastAvailableConsumptionData).subtract(1, 'day').format()
				const to = dayjs(from).endOf('day').format()

				const invoice = {
					objectId,
					from,
					to,
					price,
					isSpotMargin,
					groupBy: 'month' as const,
				}

				const results = await utils.objects.getInvoiceAccumulation.fetch(invoice)
				return results[0]?.invoiceAccumulationInEur
			}
			case 'lastWeek': {
				const from = dayjs().subtract(1, 'week').startOf('isoWeek').format()
				const to = dayjs().subtract(1, 'week').endOf('isoWeek').format()

				const results = await utils.objects.getInvoiceAccumulation.fetch({
					objectId,
					from,
					to,
					price,
					isSpotMargin,
					groupBy: 'month' as const,
				})
				return results[0]?.invoiceAccumulationInEur
			}
			case 'thisWeek': {
				//To prevent unnecessary fetch for 0 value
				if (dayjs().startOf('isoWeek').isAfter(lastAvailableConsumptionData)) return 0

				const from = dayjs().startOf('isoWeek').format()
				const to = dayjs().endOf('isoWeek').format()

				const results = await utils.objects.getInvoiceAccumulation.fetch({
					objectId,
					from,
					to,
					price,
					isSpotMargin,
					groupBy: 'month' as const,
				})
				return results[0]?.invoiceAccumulationInEur ?? 0
			}
		}
		return null
	}

	const setMoneyCard = async (
		objectId: number,
		isSpotMargin: boolean,
		priceValue: number,
		lastAvailableConsumptionData: Dayjs
	) => {
		const [lastAvailable, beforeLastAvailable, lastWeek, thisWeek] = await Promise.all([
			getInvoiceAccData(
				objectId,
				priceValue,
				isSpotMargin,
				'lastAvailable',
				lastAvailableConsumptionData
			),
			getInvoiceAccData(
				objectId,
				priceValue,
				isSpotMargin,
				'beforeLastAvailable',
				lastAvailableConsumptionData
			),
			getInvoiceAccData(
				objectId,
				priceValue,
				isSpotMargin,
				'lastWeek',
				lastAvailableConsumptionData
			),
			getInvoiceAccData(
				objectId,
				priceValue,
				isSpotMargin,
				'thisWeek',
				lastAvailableConsumptionData
			),
		])

		setLastAvailableInvoiceAcc(lastAvailable)
		setLastWeekInvoiceAcc(lastWeek)
		setThisWeekInvoiceAcc(thisWeek)
		setBeforeLastAvailableInvoiceAcc(beforeLastAvailable)
	}

	const getSavings = () => {
		if (!inverter?.electricityEarnings?.total) {
			return { currentDay: 0, previousDay: 0, currentWeek: 0, previousWeek: 0 }
		}

		const margin = object.spotMarginal || 0

		let dayRate =
			object.electricityTransferPriceContractDayPrice ?? object.electricityTransferPricePerKwh ?? 0
		let nightRate =
			object.electricityTransferPriceContractNightPrice ??
			object.electricityTransferPricePerKwh ??
			0

		if (object.electricityTaxIncluded) {
			dayRate += electricTaxPerKwh
			nightRate += electricTaxPerKwh
		}

		const marginEuros = margin / 100
		const dayEuros = dayRate / 100
		const nightEuros = nightRate / 100

		let currentDay = 0
		let previousDay = 0
		let currentWeek = 0
		let previousWeek = 0

		inverter.electricityEarnings.total.forEach(entry => {
			const entryDate = dayjs(entry.date)
			const hour = new Date(entry.date).getHours()
			const isNight = hour < 7 || hour >= 22
			const extraCosts = entry.kwh * marginEuros + entry.kwh * (isNight ? nightEuros : dayEuros)

			if (entryDate.format('DD/MM') === dayjs().format('DD/MM')) {
				currentDay += entry.earnings + extraCosts
			} else if (entryDate.format('DD/MM') === dayjs().subtract(1, 'day').format('DD/MM')) {
				previousDay += entry.earnings + extraCosts
			}

			if (entryDate.startOf('week').isSame(dayjs().startOf('week'), 'day')) {
				currentWeek += entry.earnings + extraCosts
			} else if (
				entryDate.startOf('week').isSame(dayjs().subtract(1, 'week').startOf('week'), 'day')
			) {
				previousWeek += entry.earnings + extraCosts
			}
		})

		return { currentDay, previousDay, currentWeek, previousWeek }
	}

	const setProductionCard = async () => {
		const { currentDay, previousDay, currentWeek, previousWeek } = getSavings()

		setFirstCardContent({
			title: getAtDayString(dayjs().subtract(1, 'day').day() + 1, user!.language) ?? 'loading...',
			value: currentDay.toFixed(2) ?? 0,
			lastValue: previousDay.toFixed(2),
			positive: currentDay > previousDay,
			unit: '€',
		})
		setSecondCardContent({
			title: t('previousCard.tamaViikko'),
			value: currentWeek.toFixed(2) ?? 0,
			lastValue: previousWeek.toFixed(2),
			positive: currentWeek > previousWeek,
			unit: '€',
		})
	}

	useEffect(() => {
		if (!lastAvailableConsumptionData) return setThisWeekInvoiceAcc(null)
		if (!object.id) return setThisWeekInvoiceAcc(null)

		switch (activeView) {
			case 'consumption':
				setElectricityCard()
				break
			case 'nature':
				setNatureCard()
				break
			case 'money':
				setMoneyCard(
					object.id,
					object.price.isSpotMargin,
					object.price.value,
					lastAvailableConsumptionData
				)
				break
			case 'production':
				setProductionCard()
				break
			default:
				break
		}

		// eslint-disable-next-line
	}, [activeView, object.id, lastAvailableConsumptionData])

	const onClickRegister = () => {
		logOut()
		history.push('/register')
	}

	return (
		<IonCard id='previous' className='bg-white dark:bg-background-200-dark shadow-card'>
			<div className='switcher'>
				<div
					className={
						activeView === 'money'
							? 'circular bg-brand-primary-100-light dark:bg-brand-primary-100-dark'
							: 'circular bg-text-grey-100'
					}
				></div>
				<div
					className={
						activeView === 'consumption'
							? 'circular bg-brand-primary-100-light dark:bg-brand-primary-100-dark'
							: 'circular bg-text-grey-100'
					}
				></div>
				<div
					className={
						activeView === 'nature'
							? 'circular bg-brand-primary-100-light dark:bg-brand-primary-100-dark'
							: 'circular bg-text-grey-100'
					}
				></div>
				{user?.panelLinked && (
					<div
						className={
							activeView === 'production'
								? 'circular bg-brand-primary-100-light dark:bg-brand-primary-100-dark'
								: 'circular bg-text-grey-100'
						}
					></div>
				)}
			</div>
			{object?.type ? (
				<>
					{activeView === 'money' ? (
						<div className='holder'>
							<>
								{lastAvailableConsumptionData === null ? (
									<span className='text-text-dark dark:text-white'>{t('previousCard.viive')}</span>
								) : (
									<>
										{object.price.isUnfilled ? (
											<span className='text-text-dark dark:text-white'>
												{t('previousCard.lisaaTiedot')}
											</span>
										) : (
											<>
												<>
													{thisWeekInvoiceAcc === undefined ? (
														<div className='weekprices'>
															{Array(3)
																.fill('')
																.map((_, i) => (
																	<Skeleton
																		key={i}
																		variant='rounded'
																		width={'90%'}
																		height={'3.2rem'}
																	/>
																))}
														</div>
													) : (
														<>
															{thisWeekInvoiceAcc !== undefined && thisWeekInvoiceAcc !== null && (
																<div className='weekprices'>
																	<OverviewComparisonCard
																		title={getAtDayString(
																			Number(lastAvailableConsumptionData?.isoWeekday()),
																			user!.language
																		)}
																		currentData={lastAvailableInvoiceAcc ?? 0}
																		comparisonData={beforeLastAvailableInvoiceAcc ?? 0}
																	/>
																	<OverviewComparisonCard
																		title={t('previousCard.tamaViikko')}
																		className='center'
																		currentData={thisWeekInvoiceAcc ?? 0}
																		comparisonData={lastWeekInvoiceAcc ?? 0}
																	/>
																	<OverviewComparisonCard
																		title={t('previousCard.viimeViikko')}
																		currentData={lastWeekInvoiceAcc ?? 0}
																		comparisonData={thisWeekInvoiceAcc ?? 0}
																	/>
																</div>
															)}
														</>
													)}
												</>
											</>
										)}
									</>
								)}
							</>
						</div>
					) : activeView === 'production' ? (
						<>
							<div className='consumption-views holder'>
								<div className='weekprices'>
									{!inverter?.isLoading ? (
										<>
											<div
												onClick={() => {
													history.push('/exchange/consumption/day')
												}}
												className='consumption-view'
											>
												<OverviewComparisonCard
													title={firstCardContent.title}
													currentData={parseFloat(firstCardContent.value) ?? 0}
													comparisonData={parseFloat(firstCardContent.lastValue) ?? 0}
													unit={firstCardContent.unit}
													activeView={activeView}
												/>
											</div>
											<div
												onClick={() => {
													history.push('/exchange/consumption/week')
												}}
												className='consumption-view'
											>
												<OverviewComparisonCard
													title={t('previousCard.tamaViikko')}
													className='center'
													currentData={parseFloat(secondCardContent.value) ?? 0}
													comparisonData={parseFloat(secondCardContent.lastValue) ?? 0}
													unit={firstCardContent.unit}
													activeView={activeView}
												/>
											</div>
											<div
												onClick={() => {
													history.push('/exchange/consumption/week')
												}}
												className='consumption-view'
											>
												<OverviewComparisonCard
													title={t('previousCard.viimeViikko')}
													className='weeklyprice'
													currentData={parseFloat(secondCardContent.lastValue) ?? 0}
													comparisonData={parseFloat(firstCardContent.value) ?? 0}
													unit={firstCardContent.unit}
													activeView={activeView}
												/>
											</div>
										</>
									) : null}
								</div>
							</div>
						</>
					) : (
						<>
							<div className='consumption-views holder'>
								<div className='weekprices'>
									<div
										onClick={() => {
											history.push('/exchange/consumption/day')
										}}
										className='consumption-view'
									>
										<OverviewComparisonCard
											title={firstCardContent.title}
											currentData={parseFloat(firstCardContent.value) ?? 0}
											comparisonData={parseFloat(firstCardContent.lastValue) ?? 0}
											unit={firstCardContent.unit}
										/>
									</div>
									<div
										onClick={() => {
											history.push('/exchange/consumption/week')
										}}
										className='consumption-view'
									>
										<OverviewComparisonCard
											title={secondCardContent.title}
											className='center'
											currentData={parseFloat(secondCardContent.value) ?? 0}
											comparisonData={parseFloat(secondCardContent.lastValue) ?? 0}
											unit={firstCardContent.unit}
										/>
									</div>
									<div
										onClick={() => {
											history.push('/exchange/consumption/week')
										}}
										className='consumption-view'
									>
										<OverviewComparisonCard
											title={t('previousCard.viimeViikko')}
											className='weeklyprice'
											currentData={parseFloat(secondCardContent.lastValue) ?? 0}
											comparisonData={parseFloat(firstCardContent.value) ?? 0}
											unit={firstCardContent.unit}
										/>
									</div>
								</div>
							</div>
						</>
					)}
				</>
			) : (
				<>
					{object?.type !== undefined && (
						<div
							className='flex justify-center empty-data dark:text-white text-text-dark cursor-pointer hover:bg-gray-100'
							onClick={() =>
								isAnonymousUser ? onClickRegister() : insertModal('settings-objectBasics')
							}
						>
							{isAnonymousUser ? t('previousCard.authenticate') : t('previousCard.lisaaInfoa')}
						</div>
					)}
				</>
			)}
		</IonCard>
	)
}

export default PreviousCard
